import type { PortfolioLabInvestmentItem } from "../../ctx";
import type { MergedCompositionRow, VirtualUuidData } from "./rows";

/**
 * A single, distinct instrument the global header search can match against.
 * `tickerName` is the stable identity / match key; `name` is the user-visible display name.
 */
export type SearchableInstrument = {
	tickerName: string;
	name: string;
};

/**
 * Flattens every loaded investment's `currentComposition` — direct instruments **and** the
 * instruments nested inside portfolios — into a list de-duplicated by ticker, resolving each
 * instrument's display name from the owning investment's `tickerInfoPool` (falling back to the
 * ticker when the pool has no entry). This is the in-memory collection fed to the fuzzy matcher.
 */
export function collectSearchableInstruments(investments: PortfolioLabInvestmentItem[]): SearchableInstrument[] {
	const byTicker = new Map<string, SearchableInstrument>();
	for (const investment of investments) {
		const pool = investment.tickerInfoPool;
		const addInstrument = (tickerName: string): void => {
			if (byTicker.has(tickerName)) {
				return;
			}
			byTicker.set(tickerName, { tickerName, name: pool.get(tickerName)?.name ?? tickerName });
		};
		for (const entry of investment.currentComposition) {
			if (entry.type === "instrument") {
				addInstrument(entry.tickerName);
				continue;
			}
			for (const inner of entry.instruments) {
				addInstrument(inner.tickerName);
			}
		}
	}
	return Array.from(byTicker.values());
}

/** Lower-cased text the fuzzy matcher searches against: instrument name followed by its ticker. */
export function searchableTextFor(instrument: SearchableInstrument): string {
	return `${instrument.name} ${instrument.tickerName}`.toLowerCase();
}

function firstRowData(row: MergedCompositionRow): VirtualUuidData | undefined {
	for (const key in row.data) {
		return row.data[key];
	}
	return undefined;
}

/** True when the merged row represents an instrument leaf (i.e. it carries a ticker). */
export function isInstrumentLeaf(row: MergedCompositionRow): boolean {
	const data = firstRowData(row);
	return data?.rowType === "instrument" && data.tickerName != null;
}

/** The ticker of an instrument-leaf row, or `undefined` for group / portfolio / bucket rows. */
export function rowTickerOf(row: MergedCompositionRow): string | undefined {
	const data = firstRowData(row);
	if (data?.rowType !== "instrument") {
		return undefined;
	}
	return data.tickerName ?? undefined;
}

/**
 * External-filter predicate. An instrument-leaf row passes when its ticker is in the matched set;
 * every group / portfolio / subgroup / single-instrument row returns `false`. AG Grid keeps those
 * ancestor rows visible automatically whenever a descendant leaf passes (tree-data default), so
 * non-matching sibling instruments stay hidden while the context of each match remains on screen.
 */
export function doesRowPass(row: MergedCompositionRow, matchedTickers: ReadonlySet<string>): boolean {
	const ticker = rowTickerOf(row);
	if (ticker == null) {
		return false;
	}
	return matchedTickers.has(ticker);
}
